/* eslint-disable n/no-unsupported-features/node-builtins -- this is a browser environment */
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Grid, type SvgIconProps, Typography } from '@mui/material'

import { getAppStoreUrl } from '@dhl/app-store'
import { CarParts, ParcelCatInTheBox } from '@dhl/illustrations'
import { SwipeDialog } from '@dhl/swipe-dialog'
import { isNative } from '@dhl/webview'

import { useAppVersion } from './useAppVersion.js'
import { type UpdateDialogProps } from './NativeBridge.js'

const Icon: FC<SvgIconProps & { isUnsupported: boolean }> = ({ isUnsupported, ...props }) =>
  isUnsupported ? <CarParts {...props} /> : <ParcelCatInTheBox {...props} />

export const NativeUpdateDialog: FC<UpdateDialogProps> = ({
  advisedAppVersion,
  mandatoryAppVersion,
  onLoaded,
  onClickToAppStore,
  onClickConfirmUnsupported,
  onClickIgnoreUpdate,
  topLevelDomain,
}) => {
  const { t } = useTranslation()
  const { updateType, advised } = useAppVersion({
    advisedAppVersion,
    mandatoryAppVersion,
  })
  const [open, setOpen] = useState<boolean>(true)

  const rejectAppVersionKey = 'reject-app-version'

  const isMinor = updateType === 'minor'
  const isUnsupported = updateType === 'unsupported'

  const ignoreVersionUpdate = () => {
    setOpen(false)

    if (isMinor) {
      onClickIgnoreUpdate?.(updateType)
    }

    if (isUnsupported) {
      onClickConfirmUnsupported?.(updateType)
    }

    localStorage?.setItem(rejectAppVersionKey, advised)
  }

  const shouldHideDialog = (): boolean => localStorage?.getItem(rejectAppVersionKey) === advised || !updateType

  const isDeactived = !isNative() || shouldHideDialog()

  useEffect(() => {
    if (isDeactived) {
      return
    }

    onLoaded?.(updateType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isDeactived) {
    return null
  }

  return (
    <SwipeDialog open={open} closable={isMinor || isUnsupported} onClose={ignoreVersionUpdate} sx={{ zIndex: 2000 }}>
      <Box
        sx={{
          padding: ({ spacing }) => spacing(2),
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Grid container height="100%" direction="column" data-test={`native-update-dialog-${updateType}`}>
          <Grid
            container
            flexGrow={1}
            direction="column"
            alignItems="center"
            alignContent="center"
            justifyContent="center"
          >
            <Icon
              isUnsupported={isUnsupported}
              sx={{ maxWidth: 328, width: '100%', height: '100%', maxHeight: 231, display: 'block' }}
            />
            <Typography variant="h5" mt={2} textAlign="center">
              {t(`nativeBridge.update.${updateType}.title`)}
            </Typography>
            <Typography mt={1} textAlign="center">
              {t(`nativeBridge.update.${updateType}.description`)}
            </Typography>
          </Grid>
          <Grid>
            {!isUnsupported && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                href={getAppStoreUrl(topLevelDomain)}
                data-test="native-update-dialog-button-now"
                onClick={() => onClickToAppStore?.(updateType)}
              >
                {t(`nativeBridge.update.${updateType}.button_now`)}
              </Button>
            )}

            {isUnsupported && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setOpen(false)}
                data-test="native-update-dialog-button-confirm-unsupported"
              >
                {t('nativeBridge.update.unsupported.button')}
              </Button>
            )}

            {isMinor && (
              <Button
                variant="text"
                fullWidth
                sx={{ marginTop: ({ spacing }) => spacing(2) }}
                onClick={() => setOpen(false)}
                data-test="native-update-dialog-button-not-now"
              >
                {t('nativeBridge.update.minor.button_not_now')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </SwipeDialog>
  )
}
